import type { CampaignCreativesAutoGenerateGroupsResponse, Creative, CreativeGroup } from 'c-sdk';
import { FileBrowserSelectState } from 'c-components/FileBrowser/types';
import { uniq, uniqWith } from 'ramda';
import { isStaticGroup, StaticCreativeGroup } from './lib';

/**
 * The creatives page's group and selection rules: which creatives a group shows, what a drop or
 * a bulk action sends, and the select-all checkbox. Pure, so the decisions can be tested.
 */

type CreativeGroupFields = Pick<Creative, 'id' | 'creativeGroupIds' | 'lineItemsCount'>;

/**
 * Whether a creative belongs in the open group. `invalidCreativeIds` are the errorable ids
 * (strings) of the creative-level campaign errors.
 */
export const creativeVisibleInGroup = (
    creative: CreativeGroupFields,
    openGroup: number | null | undefined,
    invalidCreativeIds: string[],
) => {
    if (openGroup == null || isStaticGroup(openGroup)) {
        if (openGroup === StaticCreativeGroup.AllCreatives) return true;

        // not in any group
        if (openGroup === StaticCreativeGroup.Ungrouped)
            return creative?.creativeGroupIds?.length === 0;

        // has an error against it
        if (openGroup === StaticCreativeGroup.Invalid)
            return invalidCreativeIds.some(id => id === String(creative.id));

        // not assigned to any line item
        if (openGroup === StaticCreativeGroup.UnAssigned)
            return creative?.lineItemsCount != null && creative.lineItemsCount <= 0;
    }

    if (openGroup == null) return false;

    return (creative.creativeGroupIds ?? []).indexOf(openGroup) !== -1;
};

/** The creative count shown beside each user group. */
export const countCreativesByGroup = (
    groups: Pick<CreativeGroup, 'id'>[],
    creatives: Pick<Creative, 'creativeGroupIds'>[],
): Record<number, number> =>
    groups.reduce(
        (totals, group) => ({
            ...totals,
            [group.id]: creatives.filter(c => (c.creativeGroupIds ?? []).indexOf(group.id) !== -1)
                .length,
        }),
        {} as Record<number, number>,
    );

export const ungroupedCreativeCount = (
    creatives: Pick<Creative, 'creativeGroupIds'>[] | null | undefined,
) =>
    (creatives ?? []).reduce(
        (total, creative) => (creative.creativeGroupIds?.length === 0 ? total + 1 : total),
        0,
    );

export const unassignedCreativeCount = (
    creatives: Pick<Creative, 'lineItemsCount'>[] | null | undefined,
) =>
    (creatives ?? []).reduce(
        (total, creative) =>
            creative.lineItemsCount == null || creative.lineItemsCount <= 0 ? total + 1 : total,
        0,
    );

/** Creatives with at least one error, each counted once. */
export const invalidCreativeCount = (errors: { errorable_id: string }[]) =>
    uniq(errors.map(err => err.errorable_id)).length;

/**
 * The update sent for one creative dropped on a group:
 * - onto a static group: taken out of every group;
 * - onto the open group it is already in: taken out of that group;
 * - otherwise: added to that group, keeping its others.
 */
export const creativeGroupsAfterDrop = (
    creativeId: number,
    creative: Pick<Creative, 'creativeGroupIds'> | null | undefined,
    groupId: number | null | undefined,
    openGroup: number | null | undefined,
): Pick<Creative, 'id' | 'creativeGroupIds'> => {
    if (groupId == null || isStaticGroup(groupId)) return { id: creativeId, creativeGroupIds: [] };

    const currentGroupIds = creative?.creativeGroupIds ?? [];
    const alreadyInGroup = currentGroupIds.indexOf(groupId) !== -1;

    if (alreadyInGroup && openGroup === groupId) {
        return {
            id: creativeId,
            creativeGroupIds: uniq([...currentGroupIds].filter(gid => gid !== groupId)),
        };
    }

    return { id: creativeId, creativeGroupIds: uniq([...currentGroupIds, groupId]) };
};

/** "Move to groups": every selected creative ends up in exactly the chosen groups. */
export const moveCreativesPayload = (selected: number[], newGroups: number[] | null | undefined) =>
    selected.map(id => ({ id, creativeGroupIds: newGroups ?? [] }));

/** "Set live dates": the same range on every selected creative. */
export const creativeDatesPayload = (
    selected: number[],
    [liveDate, endDate]: [string | null, string | null],
) => selected.map(id => ({ id, live_date: liveDate, end_date: endDate }));

/** Rename is offered only when the name actually changed. */
export const canSaveCreativeRename = (newName: string, currentName: string | null | undefined) =>
    newName !== currentName;

export const selectAllState = (selected: number[], visible: number[]) => {
    if (selected.length === 0) return FileBrowserSelectState.Unchecked;
    if (selected.length === visible.length) return FileBrowserSelectState.Checked;
    return FileBrowserSelectState.Indeterminate;
};

/** Ticking select-all from empty selects everything visible; otherwise it clears. */
export const selectionAfterSelectAll = (selected: number[], visible: number[]) =>
    selectAllState(selected, visible) === FileBrowserSelectState.Unchecked
        ? uniqWith((a, b) => a === b, [...selected, ...visible])
        : [];

type AutoGroups = CampaignCreativesAutoGenerateGroupsResponse['groups'];

/** The auto-groups search. */
export const filterAutoGroups = (groups: AutoGroups, term: string | null | undefined) =>
    groups.filter(g => g.name.toLowerCase().indexOf(term ?? '') !== -1) as AutoGroups;

/** Resolution choices for auto-grouping, narrowest first; the value has no spaces. */
export const autoGroupResolutionOptions = (resolutions: string[]) =>
    resolutions
        .sort((a, b) => Number(a.split('x')?.[0] ?? '') - Number(b.split('x')?.[0] ?? ''))
        .map(res => ({ label: res, value: res.replace(/\s/g, '') }));

/** The groups to create: the ticked ones, each with the creatives it would newly take. */
export const autoGroupsCreateRequest = (groups: AutoGroups, selectedNames: string[]) =>
    groups.reduce(
        (createGroups, group) => {
            if (selectedNames.indexOf(group.name) !== -1) {
                createGroups.groups.push({ creatives: group.new, name: group.name });
            }
            return createGroups;
        },
        { groups: [] as { name: string; creatives: number[] | undefined }[] },
    );
